import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaInstagram, FaFacebookF, FaTwitter, FaWhatsapp, FaPaperPlane, FaPhone, FaEnvelope, FaMapMarkerAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { SOCIAL_LINKS } from '../../utils/constants';
import { useSettings } from '../../context/SettingsContext';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 992 : false);
  const [openSection, setOpenSection] = useState({ quick: false, service: false, touch: false });
  const { settings } = useSettings();
  const social = { ...SOCIAL_LINKS, ...(settings.social || {}) };

  // settings.social is admin-supplied and lands in a raw href, which
  // react-router does not normalise. Without this a stored
  // "javascript:..." value would run script on the shop origin the moment a
  // visitor clicked the icon, handing them rrc_token. https + host allowlist.
  const socialHref = (value) => {
    const ALLOWED_HOSTS = ['instagram.com', 'www.instagram.com', 'facebook.com', 'www.facebook.com', 'wa.me', 'api.whatsapp.com', 'whatsapp.com', 'www.whatsapp.com'];
    if (typeof value !== 'string') return '#';
    try {
      const url = new URL(value.trim());
      if (url.protocol !== 'https:') return '#';
      if (!ALLOWED_HOSTS.includes(url.hostname.toLowerCase())) return '#';
      return url.toString();
    } catch {
      return '#';
    }
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleSection = (key) => setOpenSection((prev) => ({ ...prev, [key]: !prev[key] }));
  const isOpen = (key) => !isMobile || !!openSection[key];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      toast.success('Thank you for subscribing to our newsletter!');
      setEmail('');
    }
  };

  const renderSectionHeader = (key, title) => (
    <h5
      onClick={() => isMobile && toggleSection(key)}
      style={{
        fontFamily: "'Vollkorn', serif",
        fontSize: '1.1rem',
        marginBottom: isOpen(key) ? '20px' : '0',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: isMobile ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      {title}
      {isMobile && (openSection[key] ? <FaChevronUp style={{ fontSize: '0.8rem' }} /> : <FaChevronDown style={{ fontSize: '0.8rem' }} />)}
    </h5>
  );

  const socials = [
    { icon: <FaInstagram />, href: socialHref(social.instagram), label: 'Instagram' },
    { icon: <FaFacebookF />, href: socialHref(social.facebook), label: 'Facebook' },
    { icon: <FaTwitter />, href: '#', label: 'Twitter' },
    { icon: <FaWhatsapp />, href: socialHref(social.whatsapp), label: 'WhatsApp' },
  ];

  return (
    <footer style={{ background: 'linear-gradient(180deg, #1B3A63 0%, #122845 100%)', color: '#fff', paddingTop: '70px', paddingBottom: '0' }}>
      <div className="container">
        <div className="row g-4 mb-5">
          <div className="col-lg-4 col-md-6">
            <h3 style={{ fontFamily: "'Vollkorn', serif", fontSize: '1.5rem', marginBottom: '16px', fontWeight: 700 }} className="d-flex align-items-center">
              <img src="/images/logo.png" alt="RRC Logo" style={{ width: '42px', height: '42px', objectFit: 'contain', borderRadius: '8px', background: 'rgba(255,255,255,0.92)', padding: '2px', marginRight: '12px' }} />
              Radha Rani Collection
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '20px' }}>
              Celebrating the beauty of Indian ethnic wear with our curated collection of sarees, kurtis, lehengas, and more. Every piece tells a story of tradition and elegance.
            </p>
            <div className="d-flex gap-3 flex-wrap">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  style={{
                    width: '40px', height: '40px', borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.3)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: '1rem',
                    transition: 'all 0.3s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#6B1420'; e.currentTarget.style.borderColor = '#6B1420'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
                >
                  {social.icon}
                </a>
              ))}
            </div>

            <a
              href={socialHref(social.whatsappCommunity)}
              target="_blank"
              rel="noopener noreferrer"
              className="d-inline-flex align-items-center"
              style={{
                marginTop: '18px', gap: '10px',
                padding: '10px 18px', borderRadius: '50px',
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                color: '#fff', fontWeight: 600, fontSize: '0.85rem',
                textDecoration: 'none', boxShadow: '0 4px 14px rgba(37, 211, 102, 0.3)',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <FaWhatsapp style={{ fontSize: '1.2rem' }} />
              Join Our WhatsApp Community
            </a>
          </div>

          <div className="col-lg-2 col-md-6">
            {renderSectionHeader('quick', 'Quick Links')}
            {isOpen('quick') && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  { to: '/shop', label: 'Shop All' },
                  { to: '/category/festival-collection', label: 'Festival Collection' },
                  { to: '/category/new-arrivals', label: 'New Arrivals' },
                  { to: '/category/best-sellers', label: 'Best Sellers' },
                  { to: '/offers', label: 'Offers & Coupons' },
                  { to: '/wishlist', label: 'Wishlist' },
                  { to: '/track', label: 'Track Your Order' },
                  { to: '/my-orders', label: 'My Orders' },
                ].map((link) => (
                  <li key={link.to} style={{ marginBottom: '10px' }}>
                    <Link to={link.to} style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', transition: 'color 0.2s' }} onMouseEnter={(e) => e.target.style.color = '#fff'} onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.7)'}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="col-lg-3 col-md-6">
            {renderSectionHeader('service', 'Customer Service')}
            {isOpen('service') && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  { to: '/shipping', label: 'Shipping Policy' },
                  { to: '/privacy', label: 'Privacy Policy' },
                  { to: '/terms', label: 'Terms & Conditions' },
                  { to: '/faq', label: 'FAQ' },
                ].map((link) => (
                  <li key={link.to} style={{ marginBottom: '10px' }}>
                    <Link to={link.to} style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', transition: 'color 0.2s' }} onMouseEnter={(e) => e.target.style.color = '#fff'} onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.7)'}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="col-lg-3 col-md-6">
            {renderSectionHeader('touch', 'Get in Touch')}
            {isOpen('touch') && (
              <div>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <FaMapMarkerAlt style={{ marginTop: '4px', color: 'rgba(255,255,255,0.6)', flexShrink: 0 }} />
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>Shop No 5, Dev Plaza, Nagar Nigam Road, Near ICICI Bank</span>
                </div>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaPhone style={{ color: 'rgba(255,255,255,0.6)', flexShrink: 0 }} />
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>+91 87408 78580</span>
                </div>
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaEnvelope style={{ color: 'rgba(255,255,255,0.6)', flexShrink: 0 }} />
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', wordBreak: 'break-word' }}>radharanicollection8785@gmail.com</span>
                </div>

                <h6 style={{ fontFamily: "'Vollkorn', serif", marginBottom: '12px', fontSize: '0.95rem' }}>Newsletter</h6>
                <form onSubmit={handleNewsletterSubmit} className="d-flex" style={{ gap: '0' }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    style={{ flex: 1, minWidth: 0, padding: '10px 14px', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px 0 0 6px', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.85rem', outline: 'none' }}
                  />
                  <button type="submit" style={{ padding: '10px 16px', background: 'linear-gradient(135deg, #6B1420, #8a1a2a)', color: '#fff', border: 'none', borderRadius: '0 6px 6px 0', fontSize: '0.9rem' }}>
                    <FaPaperPlane />
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '20px 0', textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', margin: 0 }}>
            &copy; {new Date().getFullYear()} Radha Rani Collection. All rights reserved. Made with love for ethnic fashion.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
